import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const loginInProgressRef = useRef(false);
  const logoutInProgressRef = useRef(false);

  // Fetch profile from Supabase profiles table
  const fetchProfile = useCallback(async (supabaseUser) => {
    if (!supabaseUser) {
      setUser(null);
      return null;
    }
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', supabaseUser.id)
        .single();

      if (error || !profile) {
        console.error('Profile fetch error:', error);
        setUser(null);
        return null;
      }
      setUser(profile);
      return profile;
    } catch (err) {
      console.error('Profile fetch exception:', err);
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    // Get initial session
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (!mounted) return;
      setSession(s);
      if (s?.user) {
        fetchProfile(s.user).finally(() => {
          if (mounted) setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, s) => {
        if (!mounted) return;

        // If logout is in progress, just clear state and don't do anything else
        if (logoutInProgressRef.current) {
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }

        setSession(s);

        // Skip profile fetch if login() is actively handling it to prevent race condition
        if (loginInProgressRef.current) return;

        if (event === 'SIGNED_OUT') {
          setUser(null);
          setSession(null);
          setLoading(false);
          return;
        }

        if (s?.user) {
          await fetchProfile(s.user);
        } else {
          setUser(null);
        }
        setLoading(false);
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const token = session?.access_token || null;

  const login = async (email, password) => {
    loginInProgressRef.current = true;
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw new Error(error.message);

      setSession(data.session);

      // Retry with exponential backoff for RLS timing
      let profile = null;
      const delays = [0, 300, 800, 1500];
      for (const delay of delays) {
        if (delay > 0) await new Promise(r => setTimeout(r, delay));
        profile = await fetchProfile(data.user);
        if (profile) break;
      }
      if (!profile) throw new Error('Profile not found. Please contact support.');
      return profile;
    } finally {
      loginInProgressRef.current = false;
    }
  };

  const register = async (email, password, name, role, org_id) => {
    // If no org_id, create org first
    let targetOrgId = org_id;
    if (!targetOrgId) {
      const newOrgId = `org_${crypto.randomUUID().replace(/-/g, '').slice(0, 12)}`;
      const { error: orgError } = await supabase.from('organizations').insert({
        org_id: newOrgId,
        name: `${name}'s Organization`,
        status: 'active',
      });
      if (orgError) throw new Error(orgError.message);
      targetOrgId = newOrgId;
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name, role: role || 'client', org_id: targetOrgId },
      },
    });
    if (error) throw new Error(error.message);

    setSession(data.session);

    // Profile is auto-created by the database trigger
    // Retry with backoff instead of a single hardcoded wait
    let profile = null;
    const delays = [500, 800, 1200];
    for (const delay of delays) {
      await new Promise(r => setTimeout(r, delay));
      profile = await fetchProfile(data.user);
      if (profile) break;
    }
    return profile;
  };

  const loginWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin + '/dashboard',
      },
    });
    if (error) throw new Error(error.message);
  };

  const logout = async () => {
    logoutInProgressRef.current = true;
    // Clear state first so UI updates immediately
    setUser(null);
    setSession(null);
    // Then sign out from Supabase (don't block on failure)
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.error('Sign out error:', e);
    } finally {
      logoutInProgressRef.current = false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, loginWithGoogle, logout, supabase }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
